/**
 * 「先排好再顯示」閘門（AGUI-1302）：切換到含可操作互動畫面（MCP Apps iframe）的對話時，
 * 訊息區先隱藏，在看不見的狀態下貼底、等互動畫面就緒，排好後才一次顯示（比照 ChatGPT app）。
 *
 * 為什麼需要：互動畫面是 iframe，手指落在 iframe 上滑動時，觸控事件只送進 iframe、外層 document
 * 收不到，stickToBottom 因此判斷不出「使用者自己在往上捲」，會一直把畫面拉回底部。iframe 又要
 * 讀資源、交握後才撐開高度，貼底期間高度一直在變。隱藏期間使用者碰不到訊息區，衝突自然消失；
 * 使用者也看不到捲動過程，只看到已經停在最底部的結果。
 *
 * 顯示條件（先到者為準）：
 *   1. 呼叫端回報貼底已結束（stickDone），且容器內已沒有載入中的互動畫面，再等高度穩定一小段時間
 *   2. 時間上限：互動畫面再慢或載入失敗都一定顯示（失敗時畫面上本來就有錯誤訊息與重試鈕）
 */
import { startStickToBottom } from './stickToBottom';

/** 不論如何都會顯示的時間上限 */
export const REVEAL_MAX_MS = 3000;
/** 互動畫面就緒後，高度連續這麼久沒變化就顯示（iframe 交握後還會回報一次實際高度） */
export const REVEAL_FRAME_SETTLE_MS = 300;
/** 載入中的互動畫面（由 McpAppFrame 外框的 data-mcp-state 標示） */
const LOADING_SELECTOR = '[data-mcp-state="loading"]';
/** 沒有 MutationObserver 的舊 WebView：退回輪詢的間隔 */
const POLL_INTERVAL_MS = 100;

/**
 * 容器內是否還有載入中（尚未完成 ui/initialize）的互動畫面。
 *
 * @param {HTMLElement} container 捲動容器（.messages）
 * @returns {boolean}
 */
export function hasLoadingMcpApp(container) {
  return !!container.querySelector(LOADING_SELECTOR);
}

/**
 * 啟動顯示閘門。呼叫端負責在啟動前把訊息區隱藏，並在 onReveal 時取消隱藏。
 *
 * @param {HTMLElement} container 捲動容器（.messages）
 * @param {object} options
 * @param {() => void} options.scrollToEnd 把容器捲到內容底部（與貼底使用同一個終點）
 * @param {(reason: string, elapsedMs: number) => void} options.onReveal 該顯示時呼叫一次；
 *   reason 為 ready（排好了）／max（逾時）／cancel（被呼叫端中止）
 * @param {number} [options.maxMs] 時間上限
 * @param {number} [options.frameSettleMs] 互動畫面就緒後的高度穩定判定時間
 * @returns {{ stickDone: () => void, cancel: () => void }}
 *   stickDone：回報貼底已結束（可重複呼叫）；cancel：立刻顯示、不再捲動（切換對話、送出新提問、容器卸載時用）
 */
export function startRevealGate(container, {
  scrollToEnd,
  onReveal,
  maxMs = REVEAL_MAX_MS,
  frameSettleMs = REVEAL_FRAME_SETTLE_MS,
}) {
  const startedAt = Date.now();
  let done = false;
  let stickFinished = false;
  let mutationObserver = null;
  let pollTimer = null;
  // 互動畫面就緒後的短暫貼底（追最後幾次高度變化）；有值代表已進入最後階段
  let settleStop = null;
  let maxTimer = null;

  const stopWatching = () => {
    mutationObserver?.disconnect();
    mutationObserver = null;
    clearInterval(pollTimer);
    pollTimer = null;
  };

  // 結束閘門：只執行一次。cancel 時不捲動——那時已換了對話或要讓提問置頂，捲動歸別的邏輯負責
  const finish = (reason, scroll) => {
    if (done) return;
    done = true;
    clearTimeout(maxTimer);
    stopWatching();
    settleStop?.();
    if (scroll) scrollToEnd();
    onReveal?.(reason, Date.now() - startedAt);
  };

  // 貼底結束後才判斷：互動畫面都不在載入中，就進入最後的短暫貼底；否則繼續等它們的狀態改變
  const check = () => {
    if (done || !stickFinished || settleStop) return;
    if (hasLoadingMcpApp(container)) {
      watchFrames();
      return;
    }
    stopWatching();
    const remaining = Math.max(0, maxMs - (Date.now() - startedAt));
    settleStop = startStickToBottom(container, {
      scrollToEnd,
      settleMs: frameSettleMs,
      maxMs: remaining,
      onStop: () => finish('ready', true),
    });
  };

  function watchFrames() {
    if (mutationObserver || pollTimer) return;
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(check);
      mutationObserver.observe(container, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-mcp-state'],
      });
    } else {
      pollTimer = setInterval(check, POLL_INTERVAL_MS);
    }
  }

  maxTimer = setTimeout(() => finish('max', true), maxMs);

  return {
    stickDone: () => {
      stickFinished = true;
      check();
    },
    cancel: () => finish('cancel', false),
  };
}
